import { Box, FormControlLabel, Radio, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FBoxContainer, FHRadio, FHRadioGroup, FHWarning } from '../../../../../components/base';
import { FRadioGroup as FRadioGroupV2 } from '../../../../../components/baseV2';
import { FORTA_BLUE } from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';

import type { ChangeEvent } from 'react';

const clientProgressValues = {
  positiveProgress: 'The client appeared to learn and make positive progress during the session',
  noProgress: 'The client made little to no progress during the session',
};

interface IClientProgressProps {
  isEditable: boolean;
  clientProgress: string;
  onChange: (key: string, value: any) => void;
  useNewUi: boolean;
}

function ClientProgress({ isEditable, clientProgress, onChange, useNewUi }: IClientProgressProps) {
  const { t } = useTranslation('sessionNotes');

  if (useNewUi) {
    return (
      <Box sx={{ my: 3, textAlign: 'left' }}>
        <FRadioGroupV2
          label={t('clientProgress.title')}
          fullWidth
          value={clientProgress}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            onChange('client_progress', e.target.value);
          }}
        >
          <FormControlLabel
            disabled={!isEditable && clientProgress !== clientProgressValues.positiveProgress}
            data-testid="checkBoxClientProgress"
            value={clientProgressValues.positiveProgress}
            control={<Radio size="small" />}
            label={t('clientProgress.fields.positiveProgress')}
          />
          <FormControlLabel
            disabled={!isEditable && clientProgress !== clientProgressValues.noProgress}
            data-testid="checkBoxClientNoProgress"
            value={clientProgressValues.noProgress}
            control={<Radio size="small" />}
            label={t('clientProgress.fields.noProgress')}
          />
        </FRadioGroupV2>
        {isEditable && (
          <FHWarning
            sxText={{ ml: 1 }}
            iconColor={FORTA_BLUE.light1}
            sxContainer={{ mt: 1.5, ml: -0.5, backgroundColor: 'transparent', p: 0 }}
            content="Attention: If the client is exhibiting a medical emergency, please call 911 immediately
              or go to the nearest emergency room."
          />
        )}
      </Box>
    );
  }

  return (
    <FBoxContainer
      sx={{
        my: 3,
        position: 'relative',
        borderRadius: '10px',
        boxShadow: 'none',
      }}
      contentStyle={{
        pt: 2.5,
      }}
    >
      <Typography fontSize={21} color={FORTA_BLUE.primary} textAlign="left">
        {t('clientProgress.title')}
      </Typography>
      {isEditable && (
        <FHWarning
          sxText={{ ml: 1 }}
          iconColor={FORTA_BLUE.light1}
          sxContainer={{ mt: 1.5, ml: -0.5, backgroundColor: 'transparent', p: 0 }}
          content="Attention: If the client is exhibiting a medical emergency, please call 911 immediately
            or go to the nearest emergency room."
        />
      )}
      <FHRadioGroup
        sx={{ mt: 2 }}
        colorTheme={Themes.Primary}
        coloredBackground
        value={clientProgress}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChange('client_progress', e.target.value);
        }}
      >
        <FormControlLabel
          disabled={!isEditable && clientProgress !== clientProgressValues.positiveProgress}
          data-testid="checkBoxClientProgress"
          sx={{
            width: 'fit-content',
            textAlign: 'left',
          }}
          value={clientProgressValues.positiveProgress}
          control={<FHRadio sx={{ '&.MuiRadio-root': { boxShadow: 'none' } }} />}
          label={t('clientProgress.fields.positiveProgress')}
        />
        <FormControlLabel
          disabled={!isEditable && clientProgress !== clientProgressValues.noProgress}
          data-testid="checkBoxClientNoProgress"
          sx={{
            width: 'fit-content',
            textAlign: 'left',
          }}
          value={clientProgressValues.noProgress}
          control={<FHRadio sx={{ '&.MuiRadio-root': { boxShadow: 'none' } }} />}
          label={t('clientProgress.fields.noProgress')}
        />
      </FHRadioGroup>
    </FBoxContainer>
  );
}

export default ClientProgress;
